import { Fragment } from 'react'
import { NextSeo } from 'next-seo'
import { useTranslation, Trans } from 'next-i18next'

import NavigationBar from '@/components/NavigationBar'
import Footer from '@/components/Footer'
import { AlternateLink } from '@/components/AlternateLink'

import Form from '@/features/landing/templates/Form'
import FAQs from '@/features/landing/templates/FAQs'
import Blogs from '@/features/landing/templates/Blogs'
import Contact from '@/features/landing/templates/Contact'
import OurTrustingClient from '@/features/landing/templates/OurTrustingClient'

import Hero from '@/features/services/templates/HeroLeft'
import ServiceInfo from '@/features/services/templates/ServiceInfo'
import ServiceReason from '@/features/services/templates/ServiceReason'
import Section from '@/features/services/templates/Section'
import TextWithImage from '@/features/services/templates/TextWithImage'
import IconWidgetList from '@/features/services/components/IconWidgetList'
import { HandoutBanner } from '@/features/resources/components/Banner'

import { logEvent } from '@/lib/gtm'

import {
  useMeta,
  useHeroData,
  useServiceInfoData,
  useFAQsData,
  useTargetData,
  useDocumentData,
  useServiceReasonData,
} from '@/features/services/constants/legal'

import { ServicePageProps } from '@/features/services/types'

function LegalPage({ blogs }: ServicePageProps): React.ReactElement {
  const { t } = useTranslation('services')

  const META = useMeta()
  const HERO = useHeroData()
  const INFO = useServiceInfoData()
  const REASON = useServiceReasonData()
  const FAQS = useFAQsData()
  const TARGET = useTargetData()
  const DOCUMENTS = useDocumentData()

  return (
    <Fragment>
      <AlternateLink />
      <NextSeo
        title={META.title}
        description={META.description}
        openGraph={{
          title: META.og.title,
          description: META.og.description,
          images: [
            {
              url: META.og.image.url,
              width: 1200,
              height: 630,
              alt: META.og.title,
            },
          ],
        }}
      />
      <NavigationBar />
      <Hero {...HERO} />
      <ServiceInfo {...INFO} />
      <Section
        title={
          <Trans t={t} i18nKey="legal.document.title">
            Legal Documents Provided by PDPA Core
          </Trans>
        }
      >
        <IconWidgetList items={DOCUMENTS} />
      </Section>
      <ServiceReason {...REASON} />
      <OurTrustingClient />
      <TextWithImage {...TARGET} />
      <HandoutBanner
        onClickDownload={() => {
          logEvent({
            ga: {
              category: 'Legal',
              action: 'Download handout',
              label: 'handoutSection',
            },
          })
        }}
      />
      <Form />
      <FAQs items={FAQS} />
      {blogs && <Blogs blogs={blogs} />}
      <Contact />
      <Footer />
    </Fragment>
  )
}

export default LegalPage
